/**
 * DisclosureRow — one row of the rail's flat Next-steps list (D-4).
 *
 * The Next-steps tab used to be five full cards stacked in a scroller: five
 * borders, five headings and roughly two screens of height for a recruiter who
 * wanted one of them. D-4 turns that into a list you can read in a glance —
 * every tool is one row with its name and (where the parent already knows it) a
 * count, and the tool itself unfolds in place underneath.
 *
 * NEVER UNMOUNTED. The body hides with the `hidden` ATTRIBUTE, it does not stop
 * rendering (the F1 rule): the cards inside own half-typed task titles, an
 * in-progress records request and a form-link picker mid-choice, and every one
 * of them also owns a POST. Unmounting on collapse would drop the typing and,
 * worse, let the next expand re-fire a request the recruiter already sent.
 *
 * Collapsed by default, no Escape handling, no state of its own beyond open —
 * the rail is layout, and the work stays in the cards.
 */
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface DisclosureRowProps {
  /** The card's own lucide icon, so the row reads as that card's front door. */
  icon: LucideIcon;
  label: string;
  /** A count or state the PARENT already has (no fetch may be added for one). */
  summary?: string | null;
  defaultOpen?: boolean;
  children: ReactNode;
}

export default function DisclosureRow({
  icon: Icon, label, summary = null, defaultOpen = false, children,
}: DisclosureRowProps) {
  const [open, setOpen] = useState(defaultOpen);
  // Stable per-instance id: the header is the accessible name for a body that
  // is a sibling, not a child, so it needs aria-controls to point at it.
  const bodyId = useId();

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex min-h-[44px] w-full items-center gap-2 px-3 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:hover:bg-gray-800/60 sm:min-h-0 sm:py-2"
      >
        <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
        <span className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
        {summary && (
          <span className="ml-auto min-w-0 truncate text-xs text-gray-400 dark:text-gray-500">{summary}</span>
        )}
        {/* The chevron takes the auto margin only when no summary claimed it —
            two `ml-auto` siblings would split the free space between them and
            park the summary in the middle of the row. */}
        <ChevronRight
          className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-150 ${summary ? '' : 'ml-auto'} ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* `hidden`, never absent — see the F1 note above. */}
      <div id={bodyId} hidden={!open} className="px-3 pb-3">
        {children}
      </div>
    </div>
  );
}
